// ============================================
// ChatButton Component
// Button to start a chat with a user/store/product
// ============================================

import React from 'react';
import { Button } from '@/components/ui/button';
import { MessagesSquare } from 'lucide-react';
import { useChatContext } from '@/context/ChatContext';
import { getOrCreateDMConversation } from '@/lib/supabase/chat';
import { useToast } from '@/hooks/use-toast';
import { useSession } from '@/context/SessionContext';
import { supabase } from '@/lib/supabase/client';

interface ChatButtonProps {
  userId: string;
  username: string;
  slug?: string;
  avatarUrl?: string | null;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
  asChild?: boolean;
  disabled?: boolean;
}

export const ChatButton: React.FC<ChatButtonProps> = ({
  userId: targetUserId,
  username,
  slug,
  avatarUrl,
  variant = 'default',
  size = 'default',
  className,
  children,
  asChild = false,
  disabled = false,
}) => {
  const { openChatWindow } = useChatContext();
  const { toast } = useToast();
  const { session } = useSession();
  const currentUserId = session?.user?.id;

  const handleStartChat = async () => {
    if (!currentUserId) {
      toast({
        title: 'Inicia sesión para chatear',
        description: 'Necesitas una cuenta activa para enviar mensajes.',
        variant: 'destructive',
      });
      return;
    }

    // Don't allow chatting with yourself
    if (currentUserId === targetUserId) {
      toast({
        title: 'No puedes chatear contigo mismo',
        variant: 'destructive',
      });
      return;
    }

    try {
      let mutualFollow = null;
      try {
        const { data } = await supabase.rpc('check_mutual_follow', { user_id_1: currentUserId, user_id_2: targetUserId }).single();
        mutualFollow = data;
      } catch (rpcErr) {
        console.warn('check_mutual_follow RPC failed:', rpcErr);
      }

      // Get or create conversation
      let conversationId: string | null = null;
      try {
        conversationId = await getOrCreateDMConversation(targetUserId);
      } catch (convErr) {
        console.error('getOrCreateDMConversation RPC failed:', convErr);
        try {
          const { data: existing, error: exErr } = await supabase
            .from('dm_conversations')
            .select('id')
            .or(
              `and(user1_id.eq.${currentUserId},user2_id.eq.${targetUserId}),and(user1_id.eq.${targetUserId},user2_id.eq.${currentUserId})`
            )
            .limit(1)
            .maybeSingle();
          if (exErr) throw exErr;
          conversationId = existing?.id || null;
        } catch (fallbackErr) {
          console.warn('Fallback conversation lookup failed:', fallbackErr);
        }
      }

      // Intenta obtener el display_name y slug del perfil
      let displayName = null;
      let userSlug = slug || null;
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, slug')
          .eq('id', targetUserId)
          .single();
        if (profile) {
          if (profile.display_name && profile.display_name.trim()) {
            displayName = profile.display_name;
          }
          if (profile.slug) {
            userSlug = profile.slug;
          }
        }
      } catch {}

      if (!conversationId) {
        throw new Error('No conversation available');
      }

      openChatWindow({
        id: `dm-${conversationId}`,
        type: 'dm',
        conversationId,
        otherUserId: targetUserId,
        otherUserSlug: userSlug || undefined,
        title: displayName || username,
        avatarUrl: avatarUrl || null,
        isMinimized: !mutualFollow,
      });
    } catch (error: any) {
      console.error('Error starting chat:', error);
      
      if (error.message?.includes('blocked')) {
        toast({
          title: 'No puedes enviar mensajes',
          description: 'Este usuario te ha bloqueado o tú lo has bloqueado.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error al iniciar chat',
          description: 'No se pudo iniciar el chat. Revisa la configuración de RPCs o intenta más tarde.',
          variant: 'destructive',
        });
      }
    }
  };

  if (asChild) {
    return (
      <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className={className}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleStartChat();
          }
        }}
        onClick={(e) => {
          if (!disabled) handleStartChat();
          else e.preventDefault();
        }}
      >
        {children || (
          <>
            <MessagesSquare className="w-4 h-4 mr-2 inline-block" />
            Mensaje
          </>
        )}
      </span>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleStartChat}
      disabled={disabled}
    >
      {children || (
        <>
          <MessagesSquare className="w-4 h-4 mr-2" />
          Mensaje
        </>
      )}
    </Button>
  );
};
